import React from 'react';
import Paper, { PaperProps } from '@mui/material/Paper';

export interface AppCardProps extends Omit<PaperProps, 'title' | 'onClick' | 'variant'> {
  children: React.ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  hoverable?: boolean;
  onClick?: () => void;
  /**
   * 'default' (the original behavior) renders a plain Tailwind-styled div —
   * used by every existing screen. 'mui' renders through real MUI Paper
   * (theme tokens via sx) instead — for screens built with the MUI-first
   * component set (see LoginPage.tsx). Same dual-mode trick AppButton.tsx
   * already uses for its own legacy vs MUI vocabulary.
   */
  variant?: 'default' | 'mui';
}

/**
 * Shared card. Same markup the app's old `components/ui/Card.tsx` used to
 * own directly (since retired), so both callers (Ticket Management's bare
 * `<AppCard>` usage and every other screen's `title`/`subtitle`/`action`/
 * `hoverable` usage) share one implementation instead of two.
 */
export const AppCard: React.FC<AppCardProps> = ({
  children,
  className = '',
  title,
  subtitle,
  action,
  hoverable = false,
  variant = 'default',
  sx,
  ...rest
}) => {
  if (variant === 'mui') {
    return (
      <Paper
        elevation={0}
        className={className}
        sx={{
          borderRadius: '16px', // rounded-2xl
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
          border: '2px solid',
          borderColor: 'grey.100',
          p: { xs: 2, sm: 3 }, // Tailwind p-4/p-6 equivalents (MUI spacing unit is 8px, not Tailwind's 4px)
          backgroundImage: 'none',
          ...sx,
        }}
        {...(rest as PaperProps)}
      >
        {children}
      </Paper>
    );
  }

  return (
    <div
      className={`bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 shadow-sm hover:shadow-md dark:shadow-none transition-all duration-200 ${hoverable ? 'hover:border-indigo-300 dark:hover:border-indigo-700 cursor-pointer hover:-translate-y-0.5' : ''} ${className}`}
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
    >
      {(title || subtitle || action) && (
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200/80 dark:border-slate-800/80 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            {title && <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white truncate">{title}</h3>}
            {subtitle && <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          {action && <div className="flex-shrink-0">{action}</div>}
        </div>
      )}
      <div className="p-4 sm:p-6">
        {children}
      </div>
    </div>
  );
};

export default AppCard;
